<template>
  <VDialog
    v-model="showSwitchLocationForReportModal"
    :max-width="650"
    class="no-print"
  >
    <WithLocations
      v-if="showSwitchLocationForReportModal"
      :items-per-page="1000"
      :filters="filtersByLocStatus"
      :view-mode="true"
    >
      <div
        slot-scope="{ locations, areLocationsLoading }"
        class="locations-list"
      >
        <VCard class="text-xs-center no-print">
          <VCardTitle class="blue darken-2 white--text">
            <VFlex xs12 mb-2><h2>{{ isResuming ? 'Resuming Location Switch' : 'Select a New Location' }}</h2></VFlex>
          </VCardTitle>

          <!-- Loading locations -->
          <div v-if="areLocationsLoading">
            <BaseSpinner
              size="medium"
              message="Loading Locations..."
              text-fg-color="#000"
            />
          </div>

          <VCardText v-else class="font-weight-medium pa-5">

            <!-- Running: progress view -->
            <div v-if="jobStatus === 'running'">
              <BaseSpinner size="medium" text-fg-color="#000" />
              <p class="mt-3">
                Updating shift {{ Math.min(jobProgress.completed + 1, jobProgress.total) }} of {{ jobProgress.total }}…
              </p>
            </div>

            <!-- Completed: handled by watcher navigation -->
            <div v-else-if="jobStatus === 'completed'">
              <p>Location switched. Redirecting…</p>
            </div>

            <!-- Failed: failure summary -->
            <div v-else-if="jobStatus === 'failed'">
              <VAlert type="error" :value="true" outline class="text-xs-left mb-3">
                <strong>Some shifts couldn't be updated.</strong>
                {{ jobProgress.failed.length }} operation(s) failed after retrying.
                You can retry below or dismiss and update manually.
              </VAlert>
              <VCardActions>
                <VSpacer />
                <BaseButton color="blue darken-2" outline small @click="retryFailed">
                  Retry Failures
                </BaseButton>
                <BaseButton color="grey" outline small @click="dismissFailure">
                  Dismiss (fix manually)
                </BaseButton>
              </VCardActions>
            </div>

            <!-- Default: location selection -->
            <template v-else>
              <VAutocomplete
                v-model="selectedLocation"
                item-text="JOB_NUMBER__IDENTIFIER"
                item-value="ID"
                placeholder="Pick a Location..."
                :items="sortLocsByIdentifier(locations)"
              />
              <div>If your desired location is not on the list, contact the system administrator to manually switch it.</div>
            </template>

          </VCardText>

          <!-- Actions: only shown on selection view -->
          <VCardActions v-if="!jobStatus" text-xs-right>
            <VSpacer />
            <BaseButton
              color="blue darken-2"
              outline
              small
              :disabled="areLocationsLoading || !selectedLocation || !report.ID"
              @click="switchLocation"
            >Save</BaseButton>
            <BaseButton
              color="orange"
              outline
              small
              @click="showSwitchLocationForReportModal = false"
            >Cancel</BaseButton>
          </VCardActions>
        </VCard>
      </div>
    </WithLocations>
  </VDialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import VDialog from '@vuetify/VDialog'
import VAutocomplete from '@vuetify/VAutocomplete'
import VAlert from '@vuetify/VAlert'
import { VCardText, VCardActions } from '@vuetify/VCard'
import WithLocations from '@dataProviders/WithLocations'
import { HumanFields } from '@constants/knack'
import _sortBy from 'lodash/sortBy'
import { createHelpers } from 'vuex-map-fields'
import { clearJob, readJob } from '@services/locationSwitchJob'
const { mapFields } = createHelpers({
  getterType: 'getReportField',
  mutationType: 'updateReportField',
})

export default {
  name: 'SwitchLocationForReportModal',
  components: {
    VDialog,
    VCardText,
    VCardActions,
    VAlert,
    VAutocomplete,
    WithLocations,
  },
  props: {
    report: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedLocation: null,
      pendingLocation: null, // full location object, used for post-navigation
      resumeManifest: null, // set when resuming from the banner; fallback for navigation
      isResuming: false,
      filtersByLocStatus: {
        match: 'or',
        rules: [
          {
            field: HumanFields.LOCATIONS.STATUS,
            operator: 'is',
            value: 'Ready To Work',
          },
          {
            field: HumanFields.LOCATIONS.STATUS,
            operator: 'is',
            value: 'Work Scheduled',
          },
          {
            field: HumanFields.LOCATIONS.STATUS,
            operator: 'is',
            value: 'Permit Approved But Not Paid Yet',
          },
        ],
      },
    }
  },
  computed: {
    ...mapFields(['showSwitchLocationForReportModal']),
    ...mapGetters(['getLocationFromId', 'getLocationSwitchJob']),
    jobStatus() {
      return this.getLocationSwitchJob.status
    },
    jobProgress() {
      const job = this.getLocationSwitchJob
      return { total: job.total, completed: job.completed, failed: job.failed }
    },
  },
  watch: {
    // Reset Vuex job state when modal closes in a non-running state
    // Prevents stale state if user dismisses via the X or overlay click
    async showSwitchLocationForReportModal(val) {
      if (!val && this.jobStatus !== 'running') {
        this.resetJobState()
        this.isResuming = false
        this.resumeManifest = null
        return
      }
      if (val) {
        const manifest = readJob(this.$route.params.reportId)
        if (manifest) {
          this.resumeManifest = manifest
          this.isResuming = true
          // getLocationFromId may return undefined if WithLocations hasn't loaded yet —
          // resumeManifest serves as the fallback for navigation in that case
          this.pendingLocation = await this.getLocationFromId(manifest.newLocationId)
          this.$store.dispatch('runLocationSwitchJob', { reportId: this.$route.params.reportId })
        }
      }
    },
    jobStatus(status) {
      if (status === 'completed') {
        const locationId = this.pendingLocation?.ID || this.resumeManifest?.newLocationId
        const locationName = this.pendingLocation?.IDENTIFIER || this.resumeManifest?.locationIdentifier
        if (!locationId) return
        this.$notify({
          title: 'Location Switched Successfully',
          text: 'Switched to ' + locationName + '.',
          type: 'success',
        })
        setTimeout(() => {
          this.resetJobState()
          this.showSwitchLocationForReportModal = false
          this.$router.push({
            name: 'Daily Report',
            params: {
              locationId,
              reportId: this.$route.params.reportId,
            },
          })
        }, 1000)
      }
    },
  },
  mounted() {
    this.selectedLocation = this.report.LOCATION?.[0]?.id ?? null
  },
  methods: {
    ...mapActions(['initLocationSwitch']),
    async switchLocation() {
      try {
        const location = await this.getLocationFromId(this.selectedLocation)
        this.pendingLocation = location
        await this.initLocationSwitch({
          reportId: this.$route.params.reportId,
          newLocation: location,
        })
      } catch (err) {
        console.error('[LocationSwitch] switchLocation failed:', err)
        this.$notify({
          title: 'Location switch failed',
          text: err.message || 'An unexpected error occurred',
          type: 'error',
        })
      }
    },
    retryFailed() {
      this.$store.dispatch('runLocationSwitchJob', {
        reportId: this.$route.params.reportId,
      })
    },
    dismissFailure() {
      clearJob(this.$route.params.reportId)
      this.$store.commit('SET_LOCATION_SWITCH_JOB', {
        status: null,
        total: 0,
        completed: 0,
        failed: [],
      })
      this.showSwitchLocationForReportModal = false
    },
    resetJobState() {
      this.$store.commit('SET_LOCATION_SWITCH_JOB', {
        status: null,
        total: 0,
        completed: 0,
        failed: [],
      })
    },
    sortLocsByIdentifier(locations) {
      return _sortBy(locations, 'JOB_NUMBER__IDENTIFIER')
    },
  },
}
</script>

<style>
@media print {
  .v-overlay--active {
    display: none !important;
  }
}
</style>
